/**
 * Backend-proxied document mail (VoiceNav ``/chat/sports-certificate/mail``).
 * Sports NOC workflow mail uses ``/chat/command`` only — not this module.
 */

import { buildVoiceNavUrl } from './apiConfig'

export type MedicalLinkApiCredentials = {
  bearerToken?: string
  piBearerToken?: string
  csrfToken?: string
}

export type SportsCertificateMailResponse = {
  success?: boolean
  message?: string
  [k: string]: unknown
}

function credentialBearerToken(creds: MedicalLinkApiCredentials): string {
  return `${creds.bearerToken || creds.piBearerToken || ''}`.trim()
}

function medilinkHeaders(creds: MedicalLinkApiCredentials): HeadersInit {
  return {
    accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${credentialBearerToken(creds)}`,
    'X-CSRF-TOKEN': (creds.csrfToken ?? '').trim(),
  }
}

/** Renew ordonnance mail only — proxied through VoiceNav backend, not MediKaLLink from the browser workflow layer. */
export async function postSportsCertificateMail(
  creds: MedicalLinkApiCredentials,
  voiceNavApiBaseUrl: string,
  body: {
    receiver_id: string
    subject: string
    message: string
    file_url?: string
    attachment_base64?: string
    attachment_filename?: string
    attachment_content_type?: string
  },
  signal?: AbortSignal,
): Promise<SportsCertificateMailResponse> {
  const res = await fetch(buildVoiceNavUrl(voiceNavApiBaseUrl, 'sportsCertificateMail'), {
    method: 'POST',
    headers: medilinkHeaders(creds),
    body: JSON.stringify(body),
    signal,
  })
  const text = await res.text().catch(() => '')
  let json: SportsCertificateMailResponse
  try {
    json = text ? (JSON.parse(text) as SportsCertificateMailResponse) : {}
  } catch {
    json = { message: text }
  }
  if (!res.ok || json.success === false) {
    const msg = (json as { message?: string }).message || text || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return json
}
